import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Plus, ArrowRight, History } from "lucide-react";

/**
 * Phase P.15.1 — Products dashboard for the manager.
 *
 * Two tabs:
 *   1. Catalog     — CRUD list of tenant products (no stock on the row —
 *                    stock lives per-staff).
 *   2. Staff Stock — matrix (row per staff × column per product) of
 *                    qty_on_hand. Clicking a cell opens a "Hand out /
 *                    adjust" popover + the transaction log for that pair.
 *
 * We deliberately did NOT split this into six sub-files — one page-scoped
 * file is easier to grep and matches the codebase's convention of
 * medium-sized pages with local sub-components.
 */
export default function Products() {
  const [tab, setTab] = useState("catalog");
  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6" data-testid="products-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-md bg-accent/10 text-accent grid place-items-center">
          <Package className="h-5 w-5" />
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Inventory</div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight">Products &amp; stock</h1>
        </div>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl mb-5">
        Manage your parts catalog and track how much each staff member is carrying.
        Staff attach these products to jobs from their portal — invoiced or off-the-books.
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="products-tabs">
          <TabsTrigger value="catalog" data-testid="products-tab-catalog">Catalog</TabsTrigger>
          <TabsTrigger value="stock"   data-testid="products-tab-stock">Staff stock</TabsTrigger>
        </TabsList>
        <TabsContent value="catalog" className="mt-4">
          <CatalogTab />
        </TabsContent>
        <TabsContent value="stock" className="mt-4">
          <StaffStockTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Catalog tab — product CRUD
// ============================================================================
function CatalogTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // Phase P.27.1 — replaces the P.27 native `window.confirm` on archive.
  const [archiveConfirm, setArchiveConfirm] = useState(null); // product row or null
  const doArchive = async (p) => {
    try { await api.delete(`/products/${p.id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setArchiveConfirm(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { q: q || undefined, include_archived: includeArchived } });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, includeArchived]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input placeholder="Search name, SKU, category…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="catalog-search" />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
        <Button size="sm" onClick={() => setEditing({})} data-testid="catalog-add-btn" className="ml-auto">
          <Plus className="h-4 w-4 mr-1" /> Add product
        </Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : items.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center text-sm text-muted-foreground">
            No products yet. Add your first product so staff can attach parts to jobs.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Name</th>
                  <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">SKU</th>
                  <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Category</th>
                  <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Price</th>
                  <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Tax</th>
                  <th className="text-right px-3 py-2 w-32"> </th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`catalog-row-${p.id}`}>
                    <td className="px-3 py-2 font-bold">
                      {p.name}
                      {p.archived && <span className="ml-1 text-[10px] mono uppercase text-muted-foreground">(archived)</span>}
                      {p.description && <div className="text-[11px] text-muted-foreground font-normal">{p.description}</div>}
                    </td>
                    <td className="px-3 py-2 mono text-xs text-muted-foreground">{p.sku || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{p.category || "—"}</td>
                    <td className="px-3 py-2 text-right mono tabular-nums">€{(p.unit_price_cents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right mono tabular-nums text-xs">{(p.tax_rate * 100).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`catalog-edit-${p.id}`}>Edit</Button>
                        {!p.archived && (
                          <Button
                            variant="outline" size="sm" className="text-rose-700"
                            onClick={() => setArchiveConfirm(p)}
                            data-testid={`catalog-archive-${p.id}`}
                          >Archive</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing !== null && (
        <ProductEditDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      <ConfirmDialog
        state={archiveConfirm && {
          title: "Archive product?",
          description: <>Archive <span className="font-semibold">&lsquo;{archiveConfirm.name}&rsquo;</span>?</>,
          subtitle: "Usage history is preserved — you can restore it later from the Archived tab.",
          confirmLabel: "Archive",
          destructive: true,
          testId: "catalog-archive-confirm",
          onConfirm: () => doArchive(archiveConfirm),
        }}
        onOpenChange={() => setArchiveConfirm(null)}
      />
    </div>
  );
}

function ProductEditDialog({ product, onClose, onSaved }) {
  const isNew = !product?.id;
  const [f, setF] = useState({
    name: product?.name || "",
    description: product?.description || "",
    unit_price_cents: product?.unit_price_cents ?? 0,
    tax_rate: product?.tax_rate ?? 0.23,
    sku: product?.sku || "",
    category: product?.category || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: f.name.trim(),
        description: f.description,
        unit_price_cents: Math.round(Number(f.unit_price_cents) || 0),
        tax_rate: Number(f.tax_rate) || 0,
        sku: f.sku || null,
        category: f.category || null,
      };
      if (isNew) await api.post("/products", body);
      else await api.patch(`/products/${product.id}`, body);
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add product" : "Edit product"}</DialogTitle>
          <DialogDescription>Parts, consumables, or anything you charge as a line item. Stock is tracked per-staff.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-form-name" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unit price (cents)</Label>
              <Input type="number" value={f.unit_price_cents} onChange={(e) => setF({ ...f, unit_price_cents: e.target.value })} data-testid="product-form-price" />
              <div className="text-xs text-muted-foreground mt-1">= €{((Number(f.unit_price_cents) || 0) / 100).toFixed(2)}</div>
            </div>
            <div>
              <Label>Tax rate</Label>
              <select
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background"
                value={f.tax_rate}
                onChange={(e) => setF({ ...f, tax_rate: Number(e.target.value) })}
              >
                <option value="0.23">23% (VAT standard)</option>
                <option value="0.135">13.5% (Reduced)</option>
                <option value="0">0% (RCT / exempt)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="product-form-save">
            {busy ? "Saving…" : (isNew ? "Create" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Staff Stock tab — matrix of staff × product with per-cell qty
// ============================================================================
function StaffStockTab() {
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cell, setCell] = useState(null); // {staff_id, product_id}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, ss] = await Promise.all([
        api.get("/staff"),
        api.get("/products"),
        api.get("/staff-stock"),
      ]);
      setStaff((s.data || []).filter((x) => !x.archived));
      setProducts((p.data || []).filter((x) => !x.archived));
      setRows(ss.data || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Build a { staff_id: { product_id: qty } } lookup for O(1) cell reads.
  const grid = useMemo(() => {
    const g = {};
    for (const r of rows) {
      if (!g[r.staff_id]) g[r.staff_id] = {};
      g[r.staff_id][r.product_id] = r.qty_on_hand;
    }
    return g;
  }, [rows]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (staff.length === 0) return <div className="text-sm text-muted-foreground">Add staff first, then hand out stock.</div>;
  if (products.length === 0) return <div className="text-sm text-muted-foreground">Add products in the Catalog tab first.</div>;

  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
        // Click any cell to hand out, adjust, or view its transaction history
      </div>
      <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest min-w-[160px] sticky left-0 bg-muted/40 z-10">Staff</th>
              {products.map((p) => (
                <th key={p.id} className="px-3 py-2 text-right mono text-[10px] uppercase tracking-widest whitespace-nowrap">
                  {p.name}{p.sku ? ` · ${p.sku}` : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`stock-row-${s.id}`}>
                <td className="px-3 py-2 font-bold sticky left-0 bg-card z-10">{s.name}</td>
                {products.map((p) => {
                  const qty = grid?.[s.id]?.[p.id] ?? 0;
                  return (
                    <td key={p.id} className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setCell({ staff_id: s.id, product_id: p.id, staff_name: s.name, product_name: p.name, qty })}
                        className={`mono tabular-nums font-bold px-2 py-1 rounded hover:bg-secondary transition-colors ${qty <= 0 ? "text-muted-foreground" : qty < 3 ? "text-amber-700" : ""}`}
                        data-testid={`stock-cell-${s.id}-${p.id}`}
                      >
                        {qty.toFixed(0)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cell && (
        <StockCellDialog
          cell={cell}
          onClose={() => setCell(null)}
          onChanged={() => { setCell(null); load(); }}
        />
      )}
    </div>
  );
}

function StockCellDialog({ cell, onClose, onChanged }) {
  const [handoutQty, setHandoutQty] = useState(1);
  const [adjustDelta, setAdjustDelta] = useState(-1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [txns, setTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingTxns(true);
      try {
        const { data } = await api.get("/staff-stock-transactions", { params: { staff_id: cell.staff_id, product_id: cell.product_id, limit: 25 } });
        setTxns(data || []);
      } catch { /* silent */ }
      finally { setLoadingTxns(false); }
    })();
  }, [cell.staff_id, cell.product_id]);

  const handout = async () => {
    const q = Number(handoutQty);
    if (!q || q <= 0) { toast.error("Quantity must be > 0"); return; }
    setBusy(true);
    try {
      await api.post("/staff-stock/handout", {
        staff_id: cell.staff_id, product_id: cell.product_id,
        quantity: q, reason,
      });
      toast.success(`Handed out ${q} × ${cell.product_name} to ${cell.staff_name}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const adjust = async () => {
    const d = Number(adjustDelta);
    if (!d) { toast.error("Delta must be non-zero"); return; }
    if (!reason.trim()) { toast.error("Reason is required for adjustments"); return; }
    setBusy(true);
    try {
      await api.post("/staff-stock/adjust", {
        staff_id: cell.staff_id, product_id: cell.product_id,
        qty_delta: d, reason,
      });
      toast.success(`Adjusted by ${d} — ${reason}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="stock-cell-dialog">
        <DialogHeader>
          <DialogTitle>{cell.staff_name} · {cell.product_name}</DialogTitle>
          <DialogDescription>
            Current stock: <span className="mono font-black">{Number(cell.qty).toFixed(0)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hand out */}
          <div className="border border-border rounded-md p-3">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">Hand out</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Quantity</Label>
                <Input type="number" min="0" step="1" value={handoutQty} onChange={(e) => setHandoutQty(e.target.value)} data-testid="stock-handout-qty" />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Reason (optional)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Monday load-out" />
              </div>
              <Button onClick={handout} disabled={busy} data-testid="stock-handout-btn">
                <ArrowRight className="h-4 w-4 mr-1" /> Hand out
              </Button>
            </div>
          </div>

          {/* Adjust (take back / correct) */}
          <div className="border border-border rounded-md p-3">
            <div className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-2">Adjust</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Delta</Label>
                <Input type="number" step="1" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} data-testid="stock-adjust-delta" />
                <div className="text-[10px] text-muted-foreground mt-0.5">Negative = take back / write off</div>
              </div>
              <div className="flex-1">
                <Label className="text-xs">Reason (required)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Returned to store" />
              </div>
              <Button variant="outline" onClick={adjust} disabled={busy} data-testid="stock-adjust-btn">Adjust</Button>
            </div>
          </div>

          {/* Transaction log */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
              <History className="h-3 w-3" /> Transaction log
            </div>
            {loadingTxns ? <div className="text-sm text-muted-foreground">Loading…</div>
              : txns.length === 0 ? <div className="text-xs text-muted-foreground italic">No transactions yet.</div>
              : (
                <div className="text-xs bg-muted/30 rounded border border-border max-h-52 overflow-y-auto">
                  {txns.map((t) => (
                    <div key={t.id} className="px-2 py-1 border-b border-border last:border-b-0 flex items-center gap-2" data-testid={`stock-txn-${t.id}`}>
                      <span className="mono text-[10px] text-muted-foreground w-32 shrink-0">{new Date(t.at).toLocaleString()}</span>
                      <span className={`mono tabular-nums font-bold w-10 text-right ${t.qty_delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {t.qty_delta > 0 ? "+" : ""}{t.qty_delta.toFixed(0)}
                      </span>
                      <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground w-24">{t.reason}</span>
                      {t.booking_id && <span className="mono text-[10px] text-accent">booking</span>}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
